'use client';

import { Star } from 'lucide-react';
import { trackSectionCta } from '@/components/GoogleAnalytics';
import { BRAND_FACTS } from '@/config/brandFacts';
import { IMAGE_NAMES } from '@/config/imageConstants';
import { getImageUrls } from '@/utils/imageUtils';

const IMAGE_CDN_BASE = process.env.NEXT_PUBLIC_IMAGE_CDN_URL || '';
const APP_IMAGE = `${IMAGE_CDN_BASE}/assets/img/mvvs-mobile-app.png`;
const appStoreUrls = getImageUrls(IMAGE_NAMES.APP_STORE_BUTTON_WEBP, IMAGE_NAMES.APP_STORE_BUTTON);
const googlePlayUrls = getImageUrls(IMAGE_NAMES.GOOGLE_PLAY_ICON_WEBP, IMAGE_NAMES.GOOGLE_PLAY_ICON);

const RATINGS = [
  { score: '4.8', store: 'App Store' },
  { score: '4.5', store: 'Google Play' },
] as const;

function Stars() {
  return (
    <span className="flex gap-0.5 text-[#F5B301]" aria-hidden="true">
      {Array.from({ length: 5 }, (_, index) => (
        <Star key={index} className="h-4 w-4 fill-current" strokeWidth={0} />
      ))}
    </span>
  );
}

export default function HomeAppSection() {
  return (
    <section className="bg-white py-16 font-sans md:py-20">
      <div className="mx-auto max-w-[1120px] px-5 md:px-8">
        <div className="flex flex-col items-center gap-10 xl:flex-row xl:items-center xl:gap-16">
          <div className="w-full xl:w-[485px] xl:shrink-0">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={APP_IMAGE}
              alt="Marathi Vadhu Var Suchak mobile app on iPhone"
              className="mx-auto block w-full md:max-w-[485px] xl:mx-0"
              style={{ height: 'auto' }}
              loading="lazy"
            />
          </div>

          <div className="w-full min-w-0 text-center xl:flex-1 xl:text-left">
            <p className="text-[13px] font-semibold uppercase tracking-[2px] text-black">Mobile app</p>
            <h2 className="mt-3 whitespace-nowrap text-[clamp(18px,6.6vw,40px)] font-bold leading-tight text-black xl:text-[40px]">
              Find your match on the go
            </h2>
            <p className="mx-auto mt-4 max-w-[460px] text-[15px] leading-relaxed text-gray-600 md:text-[16px] xl:mx-0">
              Find your perfect match on the go with our mobile app. Available on both iOS and Android platforms.
            </p>

            <div className="mt-6 flex flex-col items-center gap-3 xl:flex-row xl:justify-start xl:gap-8">
              {RATINGS.map((rating) => (
                <p key={rating.store} className="flex items-center gap-2 text-[15px] text-black md:text-[16px]">
                  <Stars />
                  <span className="font-bold">{rating.score}</span>
                  <span>{rating.store}</span>
                </p>
              ))}
            </div>

            <div className="mx-auto mt-6 flex w-full flex-col items-center gap-3 sm:flex-row sm:justify-center xl:mx-0 xl:justify-start">
              <a
                href={BRAND_FACTS.apps.ios}
                target="_blank"
                rel="noopener noreferrer"
                data-ga-tracked="true"
                onClick={() => trackSectionCta('click_app_store', 'homepage_app_section', 'Download App Store')}
                className="inline-block"
              >
                <picture>
                  <source srcSet={appStoreUrls.webpUrl} type="image/webp" />
                  <img
                    src={appStoreUrls.fallbackUrl}
                    alt="Download on the App Store"
                    width={175}
                    height={54}
                    className="rounded-[15px] border border-[#333132] transition-opacity hover:opacity-80"
                    style={{ width: 175, height: 'auto' }}
                    loading="lazy"
                  />
                </picture>
              </a>
              <a
                href={BRAND_FACTS.apps.android}
                target="_blank"
                rel="noopener noreferrer"
                data-ga-tracked="true"
                onClick={() => trackSectionCta('click_play_store', 'homepage_app_section', 'Download Play Store')}
                className="inline-block"
              >
                <picture>
                  <source srcSet={googlePlayUrls.webpUrl} type="image/webp" />
                  <img
                    src={googlePlayUrls.fallbackUrl}
                    alt="Get it on Google Play"
                    width={175}
                    height={54}
                    className="rounded-[15px] border border-[#333132] transition-opacity hover:opacity-80"
                    style={{ width: 175, height: 'auto' }}
                    loading="lazy"
                  />
                </picture>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
